import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { formatStandard } from '../utils/standardLabel';
import './AdminOrders.css';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/admin/orders');
      let filteredOrders = response.data.orders || [];
      
      if (filterStatus !== 'all') {
        filteredOrders = filteredOrders.filter(order => 
          order.status.toLowerCase() === filterStatus.toLowerCase()
        );
      }
      
      setOrders(filteredOrders);
    } catch (err) {
      setError('Failed to load orders. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setUpdatingOrderId(orderId);
      await api.put(`/orders/${orderId}/status`, { status: newStatus });
      
      // Update local state
      setOrders(orders.map(order => 
        order.id === orderId 
          ? { ...order, status: newStatus, updated_at: new Date().toISOString() }
          : order
      ));
      
      // Show success message (you can replace with a toast notification later)
      // alert(`Order status updated to ${newStatus}`);
    } catch (err) {
      console.error('Error updating order status:', err);
      alert(err.response?.data?.error || 'Failed to update order status. Please try again.');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const getStatusOptions = (currentStatus) => {
    const status = currentStatus.toLowerCase();
    // Allow all status transitions for admin flexibility
    const allStatuses = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];
    
    // Filter out current status
    return allStatuses.filter(s => s !== status);
  };

  const getStatusBadgeClass = (status) => {
    const statusLower = status.toLowerCase();
    return `status-badge status-${statusLower}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleOrderDetails = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  if (loading) {
    return (
      <div className="App">
        <Header />
        <div className="admin-orders-page">
          <div className="container">
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading orders...</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <div className="admin-orders-page">
        <div className="container">
          <div className="page-header">
            <h1>Order Management</h1>
            <p>Manage and update order statuses</p>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="orders-controls">
            <div className="filter-section">
              <label htmlFor="status-filter">Filter by Status:</label>
              <select
                id="status-filter"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="status-filter"
              >
                <option value="all">All Orders</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="orders-count">
              <span>Total: {orders.length} order(s)</span>
            </div>
          </div>

          {orders.length === 0 ? (
            <div className="empty-state">
              <p>No orders found{filterStatus !== 'all' ? ` with status "${filterStatus}"` : ''}.</p>
            </div>
          ) : (
            <div className="orders-list">
              {orders.map((order) => {
                const statusOptions = getStatusOptions(order.status);
                const items = Array.isArray(order.items) ? order.items : [];
                const totalAmount = parseFloat(order.total_amount || 0) + parseFloat(order.delivery_charge || 0);
                const isExpanded = expandedOrderId === order.id;

                return (
                  <div key={order.id} className="order-card">
                    <div className="order-card-header">
                      <div className="order-header-left">
                        <div className="order-number-section">
                          <span className="order-number-label">Order #</span>
                          <span className="order-number">{order.order_number}</span>
                        </div>
                        <div className="order-customer-info">
                          <span className="customer-name">{order.full_name || 'N/A'}</span>
                          {order.school_name && (
                            <span className="customer-school">{order.school_name}</span>
                          )}
                        </div>
                      </div>
                      <div className="order-header-right">
                        <span className={getStatusBadgeClass(order.status)}>
                          {order.status}
                        </span>
                        <span className={`payment-status-badge payment-${order.payment_status?.toLowerCase() || 'pending'}`}>
                          {order.payment_status || 'pending'}
                        </span>
                      </div>
                    </div>

                    <div className="order-card-body">
                      <div className="order-info-row">
                        <div className="info-item">
                          <span className="info-label">Amount:</span>
                          <span className="info-value">₹{totalAmount.toFixed(2)}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Date:</span>
                          <span className="info-value">{formatDate(order.created_at)}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Delivery:</span>
                          <span className="info-value">
                            {order.delivery_type === 'home_delivery' ? 'Home Delivery' : 'School Pickup'}
                          </span>
                        </div>
                      </div>

                      <div className="order-actions">
                        <div className="status-update-section">
                          <label htmlFor={`status-${order.id}`}>Update Status:</label>
                          <select
                            id={`status-${order.id}`}
                            value={order.status}
                            onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                            disabled={updatingOrderId === order.id}
                            className="status-select"
                          >
                            <option value={order.status}>
                              Current: {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                            </option>
                            {statusOptions.map(status => (
                              <option key={status} value={status}>
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                              </option>
                            ))}
                          </select>
                          {updatingOrderId === order.id && (
                            <span className="updating-indicator">Updating...</span>
                          )}
                        </div>
                        <button
                          onClick={() => toggleOrderDetails(order.id)}
                          className="toggle-details-btn"
                        >
                          {isExpanded ? 'Hide Details' : 'View Details'}
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="order-details-expanded">
                          <div className="details-section">
                            <h3>Order Items ({items.length})</h3>
                            <div className="items-list">
                              {items.length > 0 ? (
                                items.map((item, idx) => (
                                  <div key={idx} className="order-item">
                                    <span className="item-name">{item.kit_name}</span>
                                    <span className="item-standard">{formatStandard(item.standard)}</span>
                                    <span className="item-quantity">Qty: {item.quantity}</span>
                                    <span className="item-price">₹{(parseFloat(item.price) * item.quantity).toFixed(2)}</span>
                                  </div>
                                ))
                              ) : (
                                <p>No items found</p>
                              )}
                            </div>
                          </div>
                          <div className="details-section">
                            <h3>Customer Information</h3>
                            <div className="customer-details">
                              <p><strong>Name:</strong> {order.full_name || 'N/A'}</p>
                              <p><strong>Email:</strong> {order.email || 'N/A'}</p>
                              {order.delivery_address && (
                                <p><strong>Address:</strong> {order.delivery_address}</p>
                              )}
                            </div>
                          </div>
                          <div className="details-section">
                            <h3>Order Summary</h3>
                            <div className="order-summary">
                              <div className="summary-row">
                                <span>Subtotal:</span>
                                <span>₹{parseFloat(order.total_amount || 0).toFixed(2)}</span>
                              </div>
                              <div className="summary-row">
                                <span>Delivery Charge:</span>
                                <span>₹{parseFloat(order.delivery_charge || 0).toFixed(2)}</span>
                              </div>
                              <div className="summary-row total">
                                <span>Total:</span>
                                <span>₹{totalAmount.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminOrders;
